import React, { FC, useEffect, useMemo, useState } from 'react';
import { v4 as uuid } from 'uuid';
import { Form, Icon } from 'semantic-ui-react';
import { BasicDialog } from '@src/views/components/modals/BasicDialog';
import { BasicDialogContainer } from '@src/views/components/modals/BasicDialogContainer';
import { Align, SemanticUIColor, Size } from '@src/types';
import { useCentralCalls } from '@src/rest/CentralCalls';

const OFFERINGS_TABLE = 'Offerings';
const CLIENT_OFFERINGS_TABLE = 'ClientOfferings';
const CLIENT_OFFERING_SOW_TABLE = 'ClientOfferingSOWs';

interface OfferingRow {
    id: string;
    name: string;
}

interface ClientOfferingDialogProps {
    clientId: string;
    sowId: string;
    sowName: string;
    onClose: () => void;
    onSaved?: () => Promise<void> | void;
}

const ClientOfferingDialog: FC<ClientOfferingDialogProps> = ( {
    clientId,
    sowId,
    sowName,
    onClose,
    onSaved,
} ) => {
    const { TableSelect, RunInsert } = useCentralCalls();

    const [ offerings, setOfferings ]   = useState<OfferingRow[]>( [] ),
          [ offeringId, setOfferingId ] = useState( '' ),
          [ isLoading, setIsLoading ]   = useState( true ),
          [ isSaving, setIsSaving ]     = useState( false ),
          [ error, setError ]           = useState( '' );

    useEffect( () => {
        let cancelled = false;

        const loadOfferings = async () => {
            setIsLoading( true );
            try {
                const rows = await ( TableSelect( OFFERINGS_TABLE, {} ) as Promise<OfferingRow[]> );
                if( cancelled ) return;

                const safeRows = rows ?? [];
                setOfferings( safeRows );
                setOfferingId( safeRows[ 0 ]?.id ?? '' );
            } catch( err ){
                if( !cancelled ){
                    console.error( '[ClientOfferingDialog] failed to load offerings:', err );
                    setError( 'Could not load offerings. Try again.' );
                }
            } finally {
                if( !cancelled ){
                    setIsLoading( false );
                }
            }
        };

        loadOfferings();
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [] );

    const offeringOptions = useMemo(
        () => [ ...offerings ]
            .sort( ( a, b ) => a.name.localeCompare( b.name ) )
            .map( row => ( {
                key: row.id,
                value: row.id,
                text: row.name,
            } ) ),
        [ offerings ]
    );

    const handleSubmit = async () => {
        if( isSaving ) return;
        if( !clientId || !sowId ){
            setError( 'Client and SOW are required.' );
            return;
        }
        if( !offeringId ){
            setError( 'Select an offering.' );
            return;
        }

        setIsSaving( true );
        setError( '' );

        const clientOfferingId = uuid();
        const clientOfferingSowId = uuid();

        try {
            await RunInsert( CLIENT_OFFERINGS_TABLE, {
                Id: clientOfferingId,
                ClientId: clientId,
                OfferingId: offeringId,
            } );

            await RunInsert( CLIENT_OFFERING_SOW_TABLE, {
                Id: clientOfferingSowId,
                ClientOfferingId: clientOfferingId,
                SOWId: sowId,
            } );

            await onSaved?.();
            onClose();
        } catch( err ){
            console.error( '[ClientOfferingDialog] failed to save client offering link:', err );
            setError( 'Could not add offering to this SOW. Check console for details.' );
        } finally {
            setIsSaving( false );
        }
    };

    return (
        <BasicDialog
            showCancelButton
            showSubmitButton
            submitLabel={ isSaving ? 'Saving...' : 'Add to SOW' }
            submitColor={ SemanticUIColor.PURPLE }
            onCancel={ onClose }
            onSubmit={ handleSubmit }
            controlAlign={ Align.CENTER }
            className={ 'no-padding create-client-dialog client-offering-dialog' }
        >
            <BasicDialogContainer
                title={ `Add Offering — ${ sowName }` }
                titleColor={ SemanticUIColor.PURPLE }
                contentPadding={ [ Size.MD, Size.MD, Size.LG, Size.MD ] }
                contentMinHeight={ '28dvh' }
                overflow
                onClose={ onClose }
            >
                <Form className={ 'create-client-form client-offering-form' }>
                    { error && (
                        <p className={ 'create-client-error' }>
                            <Icon name={ 'warning circle' }/> { error }
                        </p>
                    ) }

                    <div className={ 'create-client-section' }>
                        <div className={ 'create-client-section-header' }>
                            <span>Select offering for this SOW</span>
                        </div>

                        <p className={ 'assign-consultants-help' }>
                            This creates a ClientOffering row (ClientId + OfferingId), then links it to this SOW in ClientOfferingSOW.
                        </p>

                        <Form.Select
                            label={ 'Offering' }
                            options={ offeringOptions }
                            value={ offeringId }
                            placeholder={ isLoading ? 'Loading offerings...' : 'Select offering' }
                            disabled={ isLoading || offerings.length === 0 || isSaving }
                            onChange={ ( _e, data ) => {
                                setOfferingId( String( data.value ?? '' ) );
                                if( error ) setError( '' );
                            } }
                        />

                        { !isLoading && offerings.length === 0 && (
                            <p className={ 'assign-consultants-help' }>
                                No offerings found in the Offering table. Add one in Offerings first.
                            </p>
                        ) }
                    </div>
                </Form>
            </BasicDialogContainer>
        </BasicDialog>
    );
};

export default ClientOfferingDialog;